import { GraphQLError } from "graphql"
import { groq } from "next-sanity"

import { AlgoliaSearch } from "@/services/algoliaSearch"
import { AppSettings } from "@/settings"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { groqArticleProjection } from "../../shared/sanity/article.projection"
import { getEnrichedArticlesFromSanityObject } from "../../shared/utils/getArticleInformation"
import { getArticlesFromSkuArray } from "../../shared/utils/getArticles"
import { groqQueryOptions } from "../../shared/utils/groqQueryOptions"

/**
 * Retrieves articles based on the provided arguments.
 *
 * @param {Object} parent - The parent object.
 * @param {Object} args - The arguments object.
 * @param {Object} context - The context object.
 * @param {Object} info - The info object.
 * @returns {Object} - The articles and related information.
 */
export const queryArticlesSearch = {
  async articlesSearch(parent, args, context, info) {
    const {
      offset = 0,
      limit = 25,
      lang = AppSettings.defaultLocale(),
      cdn = true,
      loggedInBranch,
      search,
      selectedFacets,
      page,
    } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.ARTICLES,
      operation: Operations.READ,
    })

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const searchReadyFacets = mapSearchReadyFacets(selectedFacets)

    const searchString = search?.searchString

    if (!searchString) throw new GraphQLError("Invalid search string for selected field")

    const algoliaClient = AlgoliaSearch.client()

    const usedPage = page ? Number(page) - 1 : 0

    try {
      const hasArticles = await algoliaClient.searchSingleIndex({
        indexName: "articles",
        searchParams: { query: "" },
      })
      if (!hasArticles) throw new Error("No articles found")

      // const articlesResponse = await articlesIndex.searchSingleIndex(searchString, {
      //   page: usedPage,
      //   maxValuesPerFacet: 1000,
      //   clickAnalytics: true,
      //   // https://www.algolia.com/doc/api-reference/api-parameters/facets/
      //   facets: ["*"],
      //   ...(searchReadyFacets?.length > 0 && { facetFilters: searchReadyFacets }),
      //   // https://www.algolia.com/doc/api-reference/api-parameters/filters/
      //   ...(loggedInBranch?.length > 0 && {
      //     filters: `NOT notAvailableInBranchOffices:${loggedInBranch}`,
      //   }),
      // })
      const searchResponse = await algoliaClient.searchSingleIndex({
        indexName: "articles",
        searchParams: {
          query: searchString,
          page: usedPage,
          hitsPerPage: Number(limit) || 25,
          maxValuesPerFacet: 1000,
          clickAnalytics: true,
          facets: ["*"],
          ...(searchReadyFacets?.length > 0 && { facetFilters: searchReadyFacets }),
          ...(loggedInBranch?.length > 0 && {
            filters: `NOT notAvailableInBranchOffices:${loggedInBranch}`,
          }),
        },
      })
      const articleSkus = getArticleSkus(searchResponse)
      const sanityArticles = await getArticlesFromSkuArray(client, articleSkus)

      const articles = await getEnrichedArticlesFromSanityObject({
        sanityArticles,
        algoliaArticles: searchResponse?.hits,
        loggedInBranch,
        skus: articleSkus,
      })

      const facets = getFacets(searchResponse?.facets)

      return {
        items: await Promise.all(
          articles
            .filter((item) => (item.type === "article" ? item.sku : true))
            .map(async (it) =>
              it.type === "bundle"
                ? {
                    __typename: "Bundle",
                    ...it,
                    variants: await getArticlesFromSkuArray(
                      client,
                      it.variants.map((variant) => variant.sku).filter(Boolean),
                    ),
                  }
                : { __typename: "Article", ...it },
            ),
        ),
        facets,
        offset,
        limit: Number(limit) || searchResponse?.hitsPerPage || 25,
        total: searchResponse?.nbHits || 0,
        count: articles?.length || 0,
        totalPages: searchResponse?.nbPages || 0,
      }
    } catch (error) {
      console.warn("Algolia search failed", error)
      // in case algolia is down or values are missing, fallback to sanity
      const articles = await getSanityArticles({
        searchString,
        page: usedPage,
        limit: Number(limit) || 25,
        loggedInBranch,
        client,
      })
      return articles
    }
  },
}

const getArticleSkus = (response) => {
  if (!response || response?.hits?.lenght === 0) return []

  return response.hits.map(({ sku }) => sku)
}

const getFacets = (facets) => {
  if (!facets || facets.length === 0) return []

  const mappedFacets = []

  for (const [key, value] of Object.entries(facets)) {
    const keyFacet = []
    for (const [valueKey, valueAmount] of Object.entries(value)) {
      keyFacet.push({
        option: valueKey,
        amount: valueAmount,
      })
    }
    mappedFacets.push({ key, values: keyFacet })
  }

  return mappedFacets
}

const mapSearchReadyFacets = (selectedFacets) => {
  if (!selectedFacets || selectedFacets.length === 0) return []

  let facetFilters = []

  for (const { key, value } of selectedFacets) {
    if (!key || !value || value.length === 0) continue

    if (value.length > 1) {
      facetFilters.push(value.map((facet) => `${key}:${facet?.value}`))
      continue
    }

    facetFilters.push(`${key}:${value[0]?.value}`)
  }

  return facetFilters
}

/**
 *
 * @param {{
 * searchString: string,
 * page: number|string,
 * limit: number,
 * loggedInBranch: null|string|number,
 * client: SanityClient
 * }}
 * @returns {Promise<{
 * items: array,
 * facets: [],
 * offset: number,
 * limit: number,
 * total: number,
 * count: number,
 * totalPages: number,
 * }>}
 */
const getSanityArticles = async ({ searchString, page, limit = 25, loggedInBranch, client }) => {
  const isNumber = !isNaN(searchString)

  let matchers = []
  const matchPrefix = `match '*'+$term+'*'`

  // different query based if search string that was passed was a string or actual number value
  if (isNumber) {
    matchers = [
      `sku ${matchPrefix}`,
      `assortment ${matchPrefix}`,
      `searchTermThree ${matchPrefix}`,
      `ean ${matchPrefix}`,
      `searchTermFour ${matchPrefix}`,
    ]
  } else {
    matchers = [
      `title ${matchPrefix}`,
      `altTitle ${matchPrefix}`,
      `articleInfo ${matchPrefix}`,
      `brand ${matchPrefix}`,
      `titleSearchTerm ${matchPrefix}`,
      `searchTermThree ${matchPrefix}`,
      `searchTermFour ${matchPrefix}`,
    ]
  }

  const searchValue = `${matchers.join(
    " || ",
  )} && !("branchOffice-${loggedInBranch}" in notAvailableInBranchOffices[]._ref)`

  const usedLimit = Number(limit) || 25
  const offset = Number(page) * usedLimit

  const { pipesAndLimits, draftsFilter } = groqQueryOptions({
    offset,
    limit: usedLimit,
    includeDrafts: true,
  })

  const totalCountQuery = groq`count(*[_type=="article" && ${searchValue}${draftsFilter}])`
  const articlesQuery = groq`*[_type=="article" && ${searchValue}${draftsFilter}]${pipesAndLimits}{
    ...,
    ${groqArticleProjection()}
  }`

  const [totalCount, articleItems] = await Promise.all([
    client.fetch(totalCountQuery, { term: searchString }),
    client.fetch(articlesQuery, { term: searchString }),
  ])

  const articles = await getEnrichedArticlesFromSanityObject({
    sanityArticles: articleItems,
    algoliaArticles: articleItems,
    loggedInBranch,
    skus: articleItems?.map(({ sku }) => sku),
  })

  // Enrich bundle variants for Sanity fallback
  const enrichedArticles = await Promise.all(
    articles.map(async (article) => {
      if (article.type === "bundle" && article.variants && article.variants.length > 0) {
        const variantSkus = article.variants.map((variant) => variant.sku).filter(Boolean)
        if (variantSkus.length > 0) {
          const variantArticles = await getArticlesFromSkuArray(client, variantSkus)
          return {
            ...article,
            variants: variantArticles,
          }
        }
      }
      return article
    }),
  )

  return {
    items: enrichedArticles,
    facets: [],
    offset,
    limit: usedLimit,
    total: totalCount,
    count: enrichedArticles?.length || 0,
    totalPages: Math.ceil(totalCount / usedLimit),
  }
}
